import React,{useEffect,useState} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackButtonClick } from '../utils/tracking';

const {FiCheckCircle,FiClock,FiPhone,FiMail,FiDownload,FiStar}=FiIcons;

const ThankYouPage=()=> {
  const [formData,setFormData]=useState(null);

  useEffect(()=> {
    const storedData=localStorage.getItem('quoteFormData');
    if (storedData) {
      setFormData(JSON.parse(storedData));
    }
  },[]);

  const nextSteps=[
    {icon: FiClock,title: 'Review & Contact',description: 'Our California specialist will review your information and contact you within 24 hours',timeframe: 'Within 24 hours'},
    {icon: FiPhone,title: 'Personalized Consultation',description: 'Discuss your pet\'s specific needs and get a customized quote',timeframe: '15-30 minute call'},
    {icon: FiCheckCircle,title: 'Instant Coverage',description: 'Choose your plan and activate coverage immediately',timeframe: 'Same day'}
  ];
  const benefits=[
    'Up to 90% reimbursement on covered expenses',
    'No breed restrictions or age limits for enrollment',
    'Coverage for accidents,illnesses,and hereditary conditions',
    'Optional wellness plans for routine care',
    'Direct payment to veterinarians available',
    'Fast claim processing (10-30 days)',
    'Behavioral therapy coverage included',
    'Lost pet advertising coverage up to $5,000'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your pet insurance quote request has been successfully submitted. We'll have your personalized Nationwide quote ready soon!
          </p>
        </motion.div>

        {/* Confirmation Details */}
        {formData && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Quote Request Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
                <div className="space-y-2 text-gray-600">
                  <p><span className="font-medium">Name:</span> {formData.firstName} {formData.lastName}</p>
                  <p><span className="font-medium">Email:</span> {formData.email}</p>
                  <p><span className="font-medium">Phone:</span> {formData.phone}</p>
                  <p><span className="font-medium">Location:</span> {formData.city},CA {formData.zipCode}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Pet Information</h3>
                <div className="space-y-2 text-gray-600">
                  <p><span className="font-medium">Pet Name:</span> {formData.petName}</p>
                  <p><span className="font-medium">Type:</span> {formData.petType}</p>
                  <p><span className="font-medium">Breed:</span> {formData.petBreed}</p>
                  <p><span className="font-medium">Age:</span> {formData.petAge}</p>
                  <p><span className="font-medium">Coverage:</span> {formData.coverage}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Happens Next?</h2>
          <div className="space-y-6">
            {nextSteps.map((step,index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={step.icon} className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-gray-600 mb-2">{step.description}</p>
                  <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                    {step.timeframe}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Immediate Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg p-8 mb-8"
        >
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Can't Wait? Get Instant Coverage!</h2>
            <p className="text-xl text-blue-100 mb-6">
              Skip the wait and start your Nationwide pet insurance policy right now. Coverage can begin as early as tomorrow!
            </p>
            <a
              href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackButtonClick('Thank You Page Finalize Policy')}
              className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors inline-block"
            >
              Finalize Your Nationwide Policy
            </a>
            <p className="mt-4 text-blue-200 text-sm">
              Use reference: Quote Request #{Date.now().toString().slice(-6)}
            </p>
          </div>
        </motion.div>

        {/* Coverage Benefits Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Your Nationwide Pet Insurance Will Include:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit,index) => (
              <div key={index} className="flex items-center space-x-3">
                <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-gray-100 rounded-lg p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Questions? We're Here to Help!</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-4">
              <SafeIcon icon={FiPhone} className="h-6 w-6 text-blue-600" />
              <div>
                <div className="font-semibold text-gray-900">Call Us</div>
                <div className="text-gray-600">(818) 555-0123</div>
                <div className="text-sm text-gray-500">Mon-Fri 8AM-8PM,Sat 9AM-5PM PST</div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <SafeIcon icon={FiMail} className="h-6 w-6 text-blue-600" />
              <div>
                <div className="font-semibold text-gray-900">Email Us</div>
                <div className="text-gray-600">info@burbankinsurance.com</div>
                <div className="text-sm text-gray-500">Response within 4 hours</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Additional Resources */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            While You Wait,Explore More
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/compare"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Compare All Plans
            </Link>
            <Link
              to="/"
              className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
            >
              Learn More About Coverage
            </Link>
          </div>
          <p className="mt-6 text-gray-600">
            Thank you for choosing Burbank Insurance Services for your Nationwide pet insurance needs!
          </p>
        </motion.div>
      </div>
    </div>
  );
};
export default ThankYouPage;